import { AddressField } from '@flos/react-ui';
import type { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useState } from 'react';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import { type CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useRecoilValue } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useHippoText } from '../../../../hooks/useHippoText';
import { getAccountHolderDataSelector } from '../../../../recoil/selectors/getAccountHolderDataSelector';
import { getAddressStreetAndNumber, getDawaIdFromkvhxUrl } from '../../../../utils/addressUtil';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { track } from '../../../../utils/trackingUtil';
import CallMeModal from '../../../../shared_components/CallMeModal/CallMeModal';
import HelpLink from '../../../../shared_components/HelpLink/HelpLink';

const ContentAddressStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const getHippoText = useHippoText();
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const [isLoading, setIsLoading] = useState(false);
    const contentCoverables =
        currentProduct.flavourName === 'Indboforsikring'
            ? coverables.privateContent ?? []
            : coverables.youthContent ?? [];
    const coverableIndex = contentCoverables?.findIndex(
        (coverable) => coverable.fixedId === currentProduct.fixedId
    );
    const currentContentCoverable = contentCoverables[coverableIndex];
    const accountData = useRecoilValue(getAccountHolderDataSelector);
    const address = currentContentCoverable.address ?? accountData.address;
    const [isValid, setIsValid] = useState<boolean>(!!address);
    const [showModal, setShowModal] = useState<boolean>(false);

    const updateAddress = (fieldEventDataProps: FieldEventDataProps): void => {
        if (!contentCoverables || fieldEventDataProps.kvhx === undefined) {
            return;
        }
        const { address, kvhxUrl } = fieldEventDataProps;
        const dawaId = getDawaIdFromkvhxUrl(kvhxUrl);

        updateCoverableInformation({
            ...currentContentCoverable,
            dawaId,
            address,
            name: getAddressStreetAndNumber(currentContentCoverable.address ?? accountData.address)
        });
    };

    const onNext = (): void => {
        setIsLoading(true);
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            [currentProduct.flavourName === 'Indboforsikring' ? 'privateContent' : 'youngContent']:
                {
                    publicId: currentContentCoverable.publicId,
                    fixedId: currentContentCoverable.fixedId,
                    dawaId: currentContentCoverable.dawaId ?? accountData.dawaId,
                    name: getAddressStreetAndNumber(
                        currentContentCoverable.address ?? accountData.address
                    )
                },
            productToUpdate: currentProduct.flavourName
        };

        updateCoverableInformation({
            ...currentContentCoverable,
            name: getAddressStreetAndNumber(currentContentCoverable.address ?? accountData.address)
        });

        void saveCoverableInformation(updatedCoverable).then(() => {
            const contentType =
                currentProduct.flavourName === 'Indboforsikring'
                    ? 'privateContent'
                    : 'youngContent';
            track(`${contentType}CoverableInformationContentAddress`, [
                {
                    dawaId: currentContentCoverable.dawaId ?? accountData.dawaId
                },
                {
                    address: currentContentCoverable.address ?? accountData.address
                }
            ]);
            goNext();
            setIsLoading(false);
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            disableNext={isLoading || !isValid}
            hidePrevious
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-PRC-ADDRESS-SUBHEADER')}
            >
                <AddressField
                    fieldEvent={(data) => {
                        updateAddress(data);
                        if (data.status) setIsValid(data.status === 'valid');
                    }}
                    defaultValue={address}
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-PRC-ADDRESS-INPUTFIELD')}
                    disabled={isLoading}
                />
                <HelpLink
                    onClick={() => {
                        setShowModal(true);
                    }}
                >
                    {getHippoText('DSS-CANNOT-FIND-ADDRESS')}
                </HelpLink>
            </CoverableDetailsTemplate>
            <CallMeModal
                title={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-HEADER')}
                description={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-SUBHEADER')}
                subdescription={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-BOOK-CALL-SUBHEADER')}
                activityCode="dss_call_request"
                showModal={showModal}
                closeModal={() => {
                    setShowModal(false);
                }}
            />
        </WizardPagetemplate>
    );
};

export default ContentAddressStep;
